import { useCallback, useEffect, useState, type UIEvent } from 'react';

const SHADOW_FADE_RANGE_PX = 24;

export const useScrollShadows = () => {
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const [topProgress, setTopProgress] = useState(0);
  const [bottomProgress, setBottomProgress] = useState(0);

  const updateShadows = useCallback((element: HTMLElement) => {
    const distanceToBottom =
      element.scrollHeight - element.clientHeight - element.scrollTop;

    setTopProgress(Math.min(element.scrollTop / SHADOW_FADE_RANGE_PX, 1));
    setBottomProgress(Math.min(distanceToBottom / SHADOW_FADE_RANGE_PX, 1));
  }, []);

  const handleScroll = (event: UIEvent<HTMLElement>) =>
    updateShadows(event.currentTarget);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }

    updateShadows(scrollElement);

    const observer = new ResizeObserver(() => updateShadows(scrollElement));
    const content = scrollElement.firstElementChild;

    observer.observe(scrollElement);

    if (content) {
      observer.observe(content);
    }

    return () => observer.disconnect();
  }, [scrollElement, updateShadows]);

  return {
    scrollRef: setScrollElement,
    topProgress,
    bottomProgress,
    handleScroll,
  };
};
